import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';

import { TrashIcon } from 'lucide-react';
import { Button, Col, FormGroup, Input, Label, Row, Switch, Select, CardHeader, Separator, CardContent, Card, CardTitle, ButtonIcon } from 'twyne-new.ui';

import { PureSwitch } from '@Components/form/Switch';
import { DragHandle } from 'components/Sortable';

import { ActionsContainer } from './utils';

const propTypes = {
  value: PropTypes.object,
  field: PropTypes.string,
  onChange: PropTypes.func,
  visibility: PropTypes.object,
  required: PropTypes.bool,
};

export default class ComponentName extends React.Component {
  state = {
    rules: {},
  }

  get visibility() {
    return this.props.visibility
  }

  componentDidMount() {
    const { required = false, actions = [] } = this.props.value || {}
    this.setState({
      rules: { required: this.props.required || required, actions },
    })
  }

  handleChange = rules => {
    rules = { ...this.state.rules, ...rules }
    this.setState({ rules })
    this.props.onChange(rules)
  }

  ValidationMessage = ({ onChange, type, message, types, disabled }) => <div className='mb-2 flex items-center gap-2 justify-between'>
    <DragHandle />
    <Select
      disabled={disabled}
      value={type}
      options={types}
      onChange={item => onChange({ type: item && item.value || null })}
    />
    <Input
      disabled={disabled}
      onChange={evt => onChange({ message: evt.target.value })}
      value={message || ''}
    />
    <ButtonIcon onClick={() => onChange(null)}>
      <TrashIcon size={16} />
    </ButtonIcon>
  </div>

  Switch = ({ disabled }) => {
    const { required } = this.state.rules

    if (disabled) {
      return <PureSwitch value={true} disabled={true} />
    }

    return <Switch
      value={required || false}
      onChange={() => this.handleChange({ required: !required })}
    />
  }

  Required = () => {
    const { field } = this.props
    const isPassword = field.startsWith("password")
    const isRegistrationConsent = ['registration_consent', 'bundled_consent'].includes(field)
    const isVisible = this.visibility.required

    useEffect(() => {
      if (!isVisible) {
        this.handleChange({ required: false })
      }
      if (isPassword) {
        this.handleChange({
          required: true,
          actions: field === "password" ? [{ type: "isRequired", message: "Is required" }] : [],
        })
      }
      if (isRegistrationConsent) {
        this.handleChange({
          required: true,
          actions: this.props.value?.actions || this.state.rules?.actions || [],
        })
      }
    }, [isVisible, isPassword, field, isRegistrationConsent])

    if (!isVisible) {
      return null
    }

    return <Col size='3'>
      <FormGroup>
        <Label>Required</Label>
        <this.Switch disabled={isPassword || isRegistrationConsent} />
      </FormGroup>
    </Col>
  }

  Validations = () => {
    const { actions } = this.state.rules
    const { validations } = this.visibility
    const onChange = actions => this.handleChange({ actions })

    const validationsValues = validations?.map(({ value }) => value).join(',')
    const types = useMemo(() => validations?.filter(item => {
      if (item.default && !(actions?.some(action => action.type === item.value))) {
        onChange([{ type: item?.value || null, disabled: item.disabled }, ...(actions || [])])
      }
      return !item.hidden
    }), [validationsValues, actions])

    const Component = useCallback(props => <this.ValidationMessage {...props} types={types} />, [types])

    return <Col size='12'>
      <Card className='pr-4!'>
        <CardContent>
          <ActionsContainer
            title='Feedback Error Messages'
            data={actions}
            onChange={onChange}
            Component={Component}
            addButtonTag={props => <Button {...props}>Add Validation</Button>}
          />
        </CardContent>
      </Card>
    </Col>
  }

  render() {
    return <>
      <this.Required />
      {this.visibility?.validations?.length ? <this.Validations /> : null}
    </>
  }
}

ComponentName.propTypes = propTypes
ComponentName.defaultProps = {}
